import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, ReactNode } from 'react';
import {
  Heading as RadixHeading,
  Text as RadixText,
  useThemeContext,
} from '@radix-ui/themes';
import clsx from 'clsx';
import { resolveColor as resolveColorUtil } from '@/utils/colorUtils';

// MUI-like variants
export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'overline'
  | 'inherit';

export type TypographyAlign =
  | 'inherit'
  | 'left'
  | 'center'
  | 'right'
  | 'justify';
export type TypographyWeight = 'light' | 'regular' | 'medium' | 'bold';
export type TypographyColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'destructive'
  | 'info'
  | 'gray'
  | 'blue'
  | 'red'
  | 'green'
  | 'teal'
  | 'inherit';

export interface TypographyProps {
  children?: ReactNode;
  variant?: TypographyVariant;
  align?: TypographyAlign;
  color?: TypographyColor;
  weight?: TypographyWeight;
  gutterBottom?: boolean;
  noWrap?: boolean;
  paragraph?: boolean;
  as?: React.ElementType;
  className?: string;
  truncate?: boolean;
}

const variantMapping: Record<
  TypographyVariant,
  { as: React.ElementType; isHeading: boolean; size: string }
> = {
  h1: { as: 'h1', isHeading: true, size: '8' },
  h2: { as: 'h2', isHeading: true, size: '7' },
  h3: { as: 'h3', isHeading: true, size: '6' },
  h4: { as: 'h4', isHeading: true, size: '5' },
  h5: { as: 'h5', isHeading: true, size: '4' },
  h6: { as: 'h6', isHeading: true, size: '3' },
  subtitle1: { as: 'h6', isHeading: false, size: '3' },
  subtitle2: { as: 'h6', isHeading: false, size: '2' },
  body1: { as: 'p', isHeading: false, size: '3' },
  body2: { as: 'p', isHeading: false, size: '2' },
  caption: { as: 'span', isHeading: false, size: '1' },
  overline: { as: 'span', isHeading: false, size: '1' },
  inherit: { as: 'span', isHeading: false, size: '2' },
};

const alignMapping: Record<
  TypographyAlign,
  ComponentPropsWithoutRef<typeof RadixText>['align']
> = {
  inherit: undefined,
  left: 'left',
  center: 'center',
  right: 'right',
  justify: undefined, // Radix does not support justify directly
};

const weightMapping: Record<
  TypographyWeight,
  ComponentPropsWithoutRef<typeof RadixText>['weight']
> = {
  light: 'light',
  regular: 'regular',
  medium: 'medium',
  bold: 'bold',
};

function resolveColor(
  color: TypographyColor,
  accentColor?: string
): {
  color: ComponentPropsWithoutRef<typeof RadixText>['color'];
  highContrast?: boolean;
} {
  // Handle special text colors first
  switch (color) {
    case 'inherit':
    case 'default':
      return { color: undefined };
    case 'secondary':
      return { color: 'gray', highContrast: true };
    // Use centralized color mapping for other colors
    default:
      return {
        color: resolveColorUtil<
          ComponentPropsWithoutRef<typeof RadixText>['color'] | undefined
        >(color, accentColor || undefined),
      };
  }
}

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      children,
      variant = 'body1',
      align = 'inherit',
      color = 'default',
      weight = 'regular',
      gutterBottom = false,
      noWrap = false,
      paragraph = false,
      as,
      className,
      truncate = false,
      ...props
    },
    ref
  ) => {
    const mapping = variantMapping[variant] || variantMapping.body1;
    const Component = as || mapping.as;
    const isHeading = mapping.isHeading;
    const size = mapping.size;
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const { color: resolvedColor, highContrast } = resolveColor(
      color,
      accentColor
    );
    const alignValue = alignMapping[align];
    const weightValue = weightMapping[weight];

    const typographyClass = clsx(
      'typography',
      `typography-variant-${variant}`,
      color && `typography-color-${color}`,
      size && `typography-size-${size}`,
      {
        'typography-gutterBottom': gutterBottom,
        'typography-noWrap': noWrap,
        'typography-paragraph': paragraph,
      },
      className
    );

    if (isHeading) {
      return (
        <RadixHeading
          as={Component as ComponentPropsWithoutRef<typeof RadixHeading>['as']}
          ref={ref as React.Ref<HTMLHeadingElement>}
          size={size as ComponentPropsWithoutRef<typeof RadixHeading>['size']}
          align={alignValue}
          color={resolvedColor}
          highContrast={highContrast}
          weight={weightValue}
          className={typographyClass}
          {...props}
        >
          {children}
        </RadixHeading>
      );
    }
    return (
      <RadixText
        as={Component as ComponentPropsWithoutRef<typeof RadixText>['as']}
        ref={ref as React.Ref<HTMLSpanElement>}
        size={size as ComponentPropsWithoutRef<typeof RadixText>['size']}
        align={alignValue}
        color={resolvedColor}
        highContrast={highContrast}
        weight={weightValue}
        truncate={truncate || noWrap}
        className={typographyClass}
        {...props}
      >
        {children}
      </RadixText>
    );
  }
);

Typography.displayName = 'Typography';
